import React, { useEffect, useMemo } from 'react';
import Header from '../../components/Header/Header';
import Card from '../../components/Card/Card';
import Button from '../../components/Button/Button';
import { getStoredUser } from '../../lib/appAuth';
import { saveBookingTrusted } from '../../lib/bookingsApi';
import { useCurrency } from '../../context/CurrencyContext';
import './ThankYou.css';

const ThankYou = ({ bookingData, onGoHome, onNavigate }) => {
  const { formatFrom } = useCurrency();
  // Default booking data if not provided
  const booking = bookingData || {
    hotelName: 'Grand Plaza Hotel',
    location: 'Paris, France',
    checkIn: new Date(Date.now() + 86400000).toISOString().split('T')[0],
    checkOut: new Date(Date.now() + 172800000).toISOString().split('T')[0],
    nights: 1,
    roomType: 'Deluxe Room',
    guests: 2,
    total: 299
  };

  const fallbackConfirmation = useMemo(
    () => Math.random().toString(36).slice(2, 11).toUpperCase(),
    []
  );
  const confirmationCode =
    booking.confirmationNumber || fallbackConfirmation;

  useEffect(() => {
    if (!bookingData) return;
    const user = getStoredUser();
    if (!user?.id || !user?.email) return;

    const dedupeKey = `tourasco_booking_saved_${user.id}_${bookingData.confirmationNumber || 'noref'}_${bookingData.checkIn}_${bookingData.checkOut}`;
    if (sessionStorage.getItem(dedupeKey) === '1') return;
    if (sessionStorage.getItem(dedupeKey) === 'pending') return;

    sessionStorage.setItem(dedupeKey, 'pending');
    saveBookingTrusted(user, bookingData)
      .then(() => {
        sessionStorage.setItem(dedupeKey, '1');
      })
      .catch((err) => {
        console.warn('Could not save booking to your account:', err);
        sessionStorage.removeItem(dedupeKey);
      });
  }, [bookingData]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const calculateNights = () => {
    if (!booking.checkIn || !booking.checkOut) return booking.nights || 1;
    const checkInDate = new Date(booking.checkIn);
    const checkOutDate = new Date(booking.checkOut);
    const diffTime = Math.abs(checkOutDate - checkInDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays || booking.nights || 1;
  };

  const nightsCount = calculateNights();

  return (
    <div className="thank-you-page">
      <Header onLogoClick={onGoHome} onNavigate={onNavigate} />
      <main className="thank-you-main">
        <div className="thank-you-container">
          {/* Success Icon */}
          <div className="thank-you__icon-wrapper">
            <div className="thank-you__icon">✓</div>
          </div>

          {/* Main Message */}
          <section className="thank-you__hero">
            <h1 className="thank-you__title">Thank You for Your Booking!</h1>
            <p className="thank-you__subtitle">
              Your reservation has been confirmed. We've sent a confirmation email with all the details.
            </p>
          </section>

          {/* Booking Summary Card */}
          <section className="thank-you__summary">
            <Card variant="elevated" className="thank-you-card">
              <div className="thank-you-card__header">
                <h2 className="thank-you-card__title">Booking Confirmation</h2>
                <div className="thank-you-card__confirmation-number">
                  Confirmation #: <span>{confirmationCode}</span>
                </div>
              </div>

              <div className="thank-you-card__content">
                <div className="thank-you-card__hotel">
                  <h3 className="thank-you-card__hotel-name text-hotel-name">
                    {booking.hotelName}
                  </h3>
                  <p className="thank-you-card__hotel-location">📍 {booking.location}</p>
                </div>

                <div className="thank-you-card__divider"></div>

                <div className="thank-you-card__details">
                  <div className="thank-you-card__detail-row">
                    <span className="thank-you-card__detail-label">Check-in</span>
                    <span className="thank-you-card__detail-value">{formatDate(booking.checkIn)}</span>
                  </div>
                  <div className="thank-you-card__detail-row">
                    <span className="thank-you-card__detail-label">Check-out</span>
                    <span className="thank-you-card__detail-value">{formatDate(booking.checkOut)}</span>
                  </div>
                  <div className="thank-you-card__detail-row">
                    <span className="thank-you-card__detail-label">Duration</span>
                    <span className="thank-you-card__detail-value">{nightsCount} {nightsCount === 1 ? 'Night' : 'Nights'}</span>
                  </div>
                  <div className="thank-you-card__detail-row">
                    <span className="thank-you-card__detail-label">Room Type</span>
                    <span className="thank-you-card__detail-value">{booking.roomType}</span>
                  </div>
                  <div className="thank-you-card__detail-row">
                    <span className="thank-you-card__detail-label">Guests</span>
                    <span className="thank-you-card__detail-value">{booking.guests || 2} {booking.guests === 1 ? 'Guest' : 'Guests'}</span>
                  </div>
                </div>

                <div className="thank-you-card__divider"></div>

                <div className="thank-you-card__total">
                  <span className="thank-you-card__total-label">Total Amount</span>
                  <span className="thank-you-card__total-value text-price-large">
                    {formatFrom(booking.total || 299, booking.currency || 'GBP')}
                  </span>
                </div>
              </div>
            </Card>
          </section>

          {/* Next Steps */}
          <section className="thank-you__next-steps">
            <Card variant="outlined" className="thank-you-steps-card">
              <h3 className="thank-you-steps__title">What's Next?</h3>
              <ul className="thank-you-steps__list">
                <li className="thank-you-steps__item">
                  <span className="thank-you-steps__icon">📧</span>
                  <div className="thank-you-steps__content">
                    <strong>Confirmation Email</strong>
                    <p>Check your email for booking confirmation and details</p>
                  </div>
                </li>
                <li className="thank-you-steps__item">
                  <span className="thank-you-steps__icon">📱</span>
                  <div className="thank-you-steps__content">
                    <strong>Mobile Check-in</strong>
                    <p>Use our mobile app for quick check-in when you arrive</p>
                  </div>
                </li>
                <li className="thank-you-steps__item">
                  <span className="thank-you-steps__icon">🔄</span>
                  <div className="thank-you-steps__content">
                    <strong>Modify or Cancel</strong>
                    <p>Free cancellation up to 24 hours before check-in</p>
                  </div>
                </li>
              </ul>
            </Card>
          </section>

          {/* Action Buttons */}
          <section className="thank-you__actions">
            <Button 
              variant="cta" 
              size="lg"
              onClick={onGoHome}
            >
              Book Another Hotel
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => onNavigate?.('listing')}
            >
              Browse More Hotels
            </Button>
          </section>
        </div>
      </main>
    </div>
  );
};

export default ThankYou;
